/**
 * Secure Storage Fallbacks
 * 
 * Provides comprehensive fallback mechanisms for secure token storage,
 * handling platform differences, storage failures, and error recovery.
 */

import { secureTokenStorage, type StoredTokenData } from './SecureTokenStorage';

/**
 * Fallback storage configuration
 */
export interface FallbackStorageConfig {
  // Fallback strategies
  enableMemoryFallback: boolean;
  enableIndexedDBFallback: boolean;
  enableCookieFallback: boolean;
  
  // Error handling
  enableErrorRecovery: boolean;
  maxRetryAttempts: number;
  retryDelay: number; // milliseconds
  
  // Storage validation
  enableStorageValidation: boolean;
  validateOnRead: boolean;
  validateOnWrite: boolean;
  
  // Cleanup settings
  enableAutoCleanup: boolean;
  cleanupInterval: number; // milliseconds
  
  // Debug settings
  enableDebugLogging: boolean;
}

/**
 * Storage operation result
 */
export interface StorageOperationResult {
  success: boolean;
  method: 'primary' | 'memory' | 'indexeddb' | 'cookie' | 'none';
  error?: string;
  fallbackUsed: boolean;
}

/**
 * Fallback storage events
 */
export type FallbackStorageEvent = 
  | { type: 'fallback_activated'; data: { method: string; reason: string } }
  | { type: 'storage_recovered'; data: { method: string; duration: number } }
  | { type: 'storage_failed'; data: { method: string; error: string; willRetry: boolean } }
  | { type: 'cleanup_completed'; data: { itemsRemoved: number; method: string } }
  | { type: 'validation_failed'; data: { method: string; errors: string[] } };

export type FallbackStorageEventListener = (event: FallbackStorageEvent) => void;

/**
 * Secure Storage Fallbacks Manager
 */
export class SecureStorageFallbacks {
  private config: FallbackStorageConfig;
  private eventListeners: Set<FallbackStorageEventListener> = new Set();
  private isInitialized = false;
  
  // Fallback storage
  private memoryStorage: Map<string, string> = new Map();
  private indexedDBAvailable = false;
  private cookiesAvailable = false;
  
  // Error tracking
  private failureCount = 0;
  private lastFailureTime: string | null = null;
  private cleanupTimer: NodeJS.Timeout | null = null;

  constructor(config: Partial<FallbackStorageConfig> = {}) {
    this.config = {
      enableMemoryFallback: true,
      enableIndexedDBFallback: true,
      enableCookieFallback: false, // Cookies are less secure for tokens
      enableErrorRecovery: true,
      maxRetryAttempts: 3,
      retryDelay: 1000,
      enableStorageValidation: true,
      validateOnRead: true,
      validateOnWrite: true,
      enableAutoCleanup: true,
      cleanupInterval: 3600000, // 1 hour
      enableDebugLogging: false,
      ...config
    };
  }

  /**
   * Initialize fallback storage
   */
  async initialize(): Promise<void> {
    if (this.isInitialized) return;

    try {
      if (this.config.enableDebugLogging) {
        // Initializing Secure Storage Fallbacks
      }

      // Check available storage methods
      await this.checkStorageAvailability();

      // Start cleanup timer if enabled
      if (this.config.enableAutoCleanup) {
        this.startCleanupTimer();
      }

      this.isInitialized = true;

      if (this.config.enableDebugLogging) {
        // Secure Storage Fallbacks initialized successfully
      }
    } catch (error) {
      console.error('❌ Failed to initialize Secure Storage Fallbacks:', error);
      throw error;
    }
  }

  /**
   * Add event listener
   */
  addEventListener(listener: FallbackStorageEventListener): void {
    this.eventListeners.add(listener);
  }

  /**
   * Remove event listener
   */
  removeEventListener(listener: FallbackStorageEventListener): void {
    this.eventListeners.delete(listener);
  }

  /**
   * Emit event to all listeners
   */
  private emitEvent(event: FallbackStorageEvent): void {
    this.eventListeners.forEach(listener => {
      try {
        listener(event);
      } catch (error) {
        console.error('Error in fallback storage event listener:', error);
      }
    });
  }

  /**
   * Store tokens with fallback support
   */
  async storeTokensWithFallback(tokenData: StoredTokenData): Promise<StorageOperationResult> {
    if (!this.isInitialized) {
      await this.initialize();
    }

    // Try primary storage first
    try {
      await secureTokenStorage.storeTokens(tokenData);
      
      if (this.config.validateOnWrite) {
        const validation = await this.validateStoredData('primary');
        if (!validation.success) {
          throw new Error('Primary storage validation failed');
        }
      }

      return {
        success: true,
        method: 'primary',
        fallbackUsed: false
      };
    } catch (error) {
      if (this.config.enableDebugLogging) {
        console.warn('Primary storage failed, trying fallbacks:', error);
      }

      this.failureCount++;
      this.lastFailureTime = new Date().toISOString();

      // Try fallback methods
      const fallbackResult = await this.tryFallbackStorage('store', tokenData);
      return {
        success: fallbackResult.success,
        method: fallbackResult.method as 'primary' | 'memory' | 'indexeddb' | 'cookie' | 'none',
        fallbackUsed: true,
        error: fallbackResult.error
      };
    }
  }

  /**
   * Retrieve tokens with fallback support
   */
  async retrieveTokensWithFallback(): Promise<{ data: StoredTokenData | null; result: StorageOperationResult }> {
    if (!this.isInitialized) {
      await this.initialize();
    }

    // Try primary storage first
    try {
      const data = await secureTokenStorage.retrieveTokens();
      
      if (data && this.config.validateOnRead) {
        const validation = await this.validateStoredData('primary');
        if (!validation.success) {
          throw new Error('Primary storage validation failed');
        }
      }

      return {
        data,
        result: {
          success: true,
          method: 'primary',
          fallbackUsed: false
        }
      };
    } catch (error) {
      if (this.config.enableDebugLogging) {
        console.warn('Primary storage failed, trying fallbacks:', error);
      }

      // Try fallback methods
      const fallbackResult = await this.tryFallbackStorage('retrieve');
      return {
        data: fallbackResult.data as StoredTokenData | null,
        result: {
          success: fallbackResult.success,
          method: fallbackResult.method as 'primary' | 'memory' | 'indexeddb' | 'cookie' | 'none',
          fallbackUsed: true,
          error: fallbackResult.error
        }
      };
    }
  }

  /**
   * Clear tokens from all storage methods
   */
  async clearAllTokens(): Promise<StorageOperationResult[]> {
    const results: StorageOperationResult[] = [];

    // Clear primary storage
    try {
      await secureTokenStorage.clearTokens();
      results.push({
        success: true,
        method: 'primary',
        fallbackUsed: false
      });
    } catch (error) {
      results.push({
        success: false,
        method: 'primary',
        fallbackUsed: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      });
    }

    // Clear fallback storages
    if (this.config.enableMemoryFallback) {
      this.memoryStorage.clear();
      results.push({
        success: true,
        method: 'memory',
        fallbackUsed: true
      });
    }

    if (this.config.enableIndexedDBFallback && this.indexedDBAvailable) {
      try {
        await this.clearIndexedDBTokens();
        results.push({
          success: true,
          method: 'indexeddb',
          fallbackUsed: true
        });
      } catch (error) {
        results.push({
          success: false,
          method: 'indexeddb',
          fallbackUsed: true,
          error: error instanceof Error ? error.message : 'Unknown error'
        });
      }
    }

    if (this.config.enableCookieFallback && this.cookiesAvailable) {
      try {
        this.clearCookieTokens();
        results.push({
          success: true,
          method: 'cookie',
          fallbackUsed: true
        });
      } catch (error) {
        results.push({
          success: false,
          method: 'cookie',
          fallbackUsed: true,
          error: error instanceof Error ? error.message : 'Unknown error'
        });
      }
    }

    return results;
  }

  /**
   * Check storage availability
   */
  private async checkStorageAvailability(): Promise<void> {
    // Check IndexedDB availability
    if (this.config.enableIndexedDBFallback) {
      try {
        if (typeof window !== 'undefined' && 'indexedDB' in window) {
          // Test IndexedDB access
          const testDB = indexedDB.open('test-db', 1);
          await new Promise((resolve, reject) => {
            testDB.onsuccess = () => {
              testDB.result.close();
              resolve(true);
            };
            testDB.onerror = () => reject(testDB.error);
          });
          this.indexedDBAvailable = true;
        }
      } catch (error) {
        if (this.config.enableDebugLogging) {
          console.warn('IndexedDB not available:', error);
        }
      }
    }

    // Check cookies availability
    if (this.config.enableCookieFallback) {
      try {
        if (typeof document !== 'undefined') {
          // Test cookie access
          document.cookie = 'test=1; path=/';
          this.cookiesAvailable = document.cookie.includes('test=1');
          // Clean up test cookie
          document.cookie = 'test=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/';
        }
      } catch (error) {
        if (this.config.enableDebugLogging) {
          console.warn('Cookies not available:', error);
        }
      }
    }
  }

  /**
   * Try fallback storage methods
   */
  private async tryFallbackStorage(
    operation: 'store' | 'retrieve',
    data?: StoredTokenData
  ): Promise<{ success: boolean; method: string; data?: StoredTokenData | null; error?: string }> {
    const methods = [];

    if (this.config.enableMemoryFallback) methods.push('memory');
    if (this.config.enableIndexedDBFallback && this.indexedDBAvailable) methods.push('indexeddb');
    if (this.config.enableCookieFallback && this.cookiesAvailable) methods.push('cookie');

    for (const method of methods) {
      try {
        if (operation === 'store' && data) {
          const result = await this.storeInFallback(method, data);
          if (result) {
            this.emitEvent({
              type: 'fallback_activated',
              data: {
                method,
                reason: 'primary_storage_failed'
              }
            });

            return {
              success: true,
              method,
              data: undefined
            };
          }
        } else if (operation === 'retrieve') {
          const result = await this.retrieveFromFallback(method);
          if (result) {
            this.emitEvent({
              type: 'fallback_activated',
              data: {
                method,
                reason: 'primary_storage_failed'
              }
            });

            return {
              success: true,
              method,
              data: result
            };
          }
        }
      } catch (error) {
        this.emitEvent({
          type: 'storage_failed',
          data: {
            method,
            error: error instanceof Error ? error.message : 'Unknown error',
            willRetry: false
          }
        });
      }
    }

    return {
      success: false,
      method: 'none',
      error: 'All storage methods failed'
    };
  }

  /**
   * Store in fallback method
   */
  private async storeInFallback(method: string, data: StoredTokenData): Promise<boolean> {
    const serializedData = JSON.stringify(data);

    switch (method) {
      case 'memory':
        this.memoryStorage.set('tokens', serializedData);
        return true;

      case 'indexeddb':
        return await this.storeInIndexedDB(serializedData);

      case 'cookie':
        return this.storeInCookie(serializedData);

      default:
        return false;
    }
  }

  /**
   * Retrieve from fallback method
   */
  private async retrieveFromFallback(method: string): Promise<StoredTokenData | null> {
    let serializedData: string | null = null;

    switch (method) {
      case 'memory':
        serializedData = this.memoryStorage.get('tokens') || null;
        break;

      case 'indexeddb':
        serializedData = await this.retrieveFromIndexedDB();
        break;

      case 'cookie':
        serializedData = this.retrieveFromCookie();
        break;

      default:
        return null;
    }

    if (serializedData) {
      try {
        return JSON.parse(serializedData);
      } catch (error) {
        if (this.config.enableDebugLogging) {
          console.error('Failed to parse fallback data:', error);
        }
      }
    }

    return null;
  }

  /**
   * IndexedDB operations
   */
  private async storeInIndexedDB(data: string): Promise<boolean> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open('SecureTokenStorage', 1);
      
      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains('tokens')) {
          db.createObjectStore('tokens');
        }
      };
      
      request.onsuccess = () => {
        const db = request.result;
        const transaction = db.transaction(['tokens'], 'readwrite');
        const store = transaction.objectStore('tokens');
        
        const putRequest = store.put(data, 'current');
        putRequest.onsuccess = () => resolve(true);
        putRequest.onerror = () => reject(putRequest.error);
        
        db.close();
      };
      
      request.onerror = () => reject(request.error);
    });
  }

  private async retrieveFromIndexedDB(): Promise<string | null> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open('SecureTokenStorage', 1);
      
      request.onsuccess = () => {
        const db = request.result;
        const transaction = db.transaction(['tokens'], 'readonly');
        const store = transaction.objectStore('tokens');
        
        const getRequest = store.get('current');
        getRequest.onsuccess = () => resolve(getRequest.result || null);
        getRequest.onerror = () => reject(getRequest.error);
        
        db.close();
      };
      
      request.onerror = () => reject(request.error);
    });
  }

  private async clearIndexedDBTokens(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open('SecureTokenStorage', 1);
      
      request.onsuccess = () => {
        const db = request.result;
        const transaction = db.transaction(['tokens'], 'readwrite');
        const store = transaction.objectStore('tokens');
        
        const deleteRequest = store.delete('current');
        deleteRequest.onsuccess = () => resolve();
        deleteRequest.onerror = () => reject(deleteRequest.error);
        
        db.close();
      };
      
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Cookie operations (less secure, only for emergency fallback)
   */
  private storeInCookie(data: string): boolean {
    try {
      // Use secure, httpOnly-like settings where possible
      const expires = new Date(Date.now() + 24 * 60 * 60 * 1000); // 24 hours
      document.cookie = `secure_tokens=${encodeURIComponent(data)}; expires=${expires.toUTCString()}; path=/; secure; samesite=strict`;
      return true;
    } catch (error) {
      return false;
    }
  }

  private retrieveFromCookie(): string | null {
    try {
      const match = document.cookie.match(/secure_tokens=([^;]+)/);
      return match ? decodeURIComponent(match[1]) : null;
    } catch (error) {
      return null;
    }
  }

  private clearCookieTokens(): void {
    document.cookie = 'secure_tokens=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/';
  }

  /**
   * Validate stored data
   */
  private async validateStoredData(method: string): Promise<{ success: boolean; errors: string[] }> {
    const errors: string[] = [];

    try {
      let data: StoredTokenData | null = null;

      if (method === 'primary') {
        data = await secureTokenStorage.retrieveTokens();
      } else {
        data = await this.retrieveFromFallback(method);
      }

      if (!data) {
        errors.push('No data found');
      } else {
        // Validate required fields
        const required = ['accessToken', 'refreshToken', 'expiresAt'];
        const missing = required.filter(field => !data[field as keyof StoredTokenData]);
        if (missing.length > 0) {
          errors.push(`Missing fields: ${missing.join(', ')}`);
        }

        // Validate expiration
        try {
          const expiresAt = new Date(data.expiresAt);
          if (isNaN(expiresAt.getTime())) {
            errors.push('Invalid expiration date');
          }
        } catch (error) {
          errors.push('Invalid expiration date format');
        }
      }
    } catch (error) {
      errors.push(error instanceof Error ? error.message : 'Validation error');
    }

    const success = errors.length === 0;

    if (!success) {
      this.emitEvent({
        type: 'validation_failed',
        data: { method, errors }
      });
    }

    return { success, errors };
  }

  /**
   * Start cleanup timer
   */
  private startCleanupTimer(): void {
    this.cleanupTimer = setInterval(async () => {
      await this.performCleanup();
    }, this.config.cleanupInterval);
  }

  /**
   * Perform cleanup of invalid/expired data
   */
  private async performCleanup(): Promise<void> {
    let itemsRemoved = 0;

    try {
      // Check and clean primary storage
      const validation = await secureTokenStorage.validateTokens();
      if (!validation.isValid && validation.isExpired) {
        await secureTokenStorage.clearTokens();
        itemsRemoved++;
      }

      // Clean memory storage
      if (this.memoryStorage.has('tokens')) {
        const data = await this.retrieveFromFallback('memory');
        if (data) {
          const expiresAt = new Date(data.expiresAt);
          if (expiresAt.getTime() < Date.now()) {
            this.memoryStorage.delete('tokens');
            itemsRemoved++;
          }
        }
      }

      if (itemsRemoved > 0) {
        this.emitEvent({
          type: 'cleanup_completed',
          data: {
            itemsRemoved,
            method: 'all'
          }
        });

        if (this.config.enableDebugLogging) {
          // Cleaned up expired token entries
        }
      }
    } catch (error) {
      if (this.config.enableDebugLogging) {
        console.error('Cleanup failed:', error);
      }
    }
  }

  /**
   * Get fallback status
   */
  getStatus(): {
    isInitialized: boolean;
    availableMethods: string[];
    failureCount: number;
    lastFailureTime: string | null;
  } {
    const availableMethods = ['primary'];
    
    if (this.config.enableMemoryFallback) availableMethods.push('memory');
    if (this.config.enableIndexedDBFallback && this.indexedDBAvailable) availableMethods.push('indexeddb');
    if (this.config.enableCookieFallback && this.cookiesAvailable) availableMethods.push('cookie');

    return {
      isInitialized: this.isInitialized,
      availableMethods,
      failureCount: this.failureCount,
      lastFailureTime: this.lastFailureTime
    };
  }

  /**
   * Update configuration
   */
  updateConfig(updates: Partial<FallbackStorageConfig>): void {
    this.config = { ...this.config, ...updates };
  }

  /**
   * Get current configuration
   */
  getConfig(): FallbackStorageConfig {
    return { ...this.config };
  }

  /**
   * Cleanup resources
   */
  cleanup(): void {
    if (this.cleanupTimer) {
      clearInterval(this.cleanupTimer);
      this.cleanupTimer = null;
    }

    this.memoryStorage.clear();
    this.eventListeners.clear();
    this.isInitialized = false;

    if (this.config.enableDebugLogging) {
      // Secure Storage Fallbacks cleaned up
    }
  }
}

// Export singleton instance
export const secureStorageFallbacks = new SecureStorageFallbacks();
